import React from "react";
import { Button, iconButtonClasses, paperClasses } from "@mui/material";
import DialogWindow from "components/other/DialogWindow";
import { Trans } from "react-i18next";
import { useViewerQuery } from "queries/viewerQuery";
import { useMatch, useNavigate } from "react-router";
import { SUBSCRIPTION } from "../../../root/profile/ProfileRouter.config";
import styled from "styled-components/macro";
import { TUTORIALDONE } from "../../../root/tutorial/steps/Verdict";
import { SALE } from "sharedJs__generated/constants";
import { useSaleExpiration } from "root/profile/subscription/subscriptionHooks";
import SaleMainBlackFriday from "./SaleMainBlackFriday";
import SaleMainChristmas from "./SaleMainChristmas";
import { useGetState, useSetState } from "components/ReduxProvider";
import { useSetLastImpulse } from "hooks/useSetLastImpulse";
import SaleMainMotherLanguage from "./SaleMainMotherLanguage";
import SaleMainSummer from "./SaleMainSummer";
import { useMatchMain } from "root/RootRouter";

const forSales = [SALE.blackFriday, SALE.christmasFamily, SALE.motherLangDay, SALE.summer];

const SDialogWindow = styled(DialogWindow)<{ $saleId?: SALE }>`
  .${paperClasses.root} {
    ${({ $saleId, theme }) => {
      switch ($saleId) {
        case SALE.christmasFamily:
          return "background: linear-gradient(135deg, #0f0c29, #1b5e20, #ff8f00); color: white;";
        case SALE.motherLangDay:
        case SALE.summer:
          return `background: ${theme.duo.color.white}; color: black;`;
        case SALE.blackFriday:
        default:
          return "background: black; color: white;";
      }
    }};
    border: none;
  }
  .${iconButtonClasses.root} {
    color: ${({ $saleId }) => ($saleId === SALE.motherLangDay ? "black" : "white")};
  }
`;
const Hero = styled.div`
  text-align: center;
  margin: 20px 0;
`;
const SButton = styled(Button)`
  margin-bottom: 40px;
`;

const SaleMainDialog = () => {
  const { viewer, subscriptionFlags, hintWasSeen } = useViewerQuery();
  const [open, setOpen] = React.useState(false);
  const lastSeen = useGetState("saleMainDialogLastSeen");
  const setLastSeen = useSetState("saleMainDialogLastSeen");
  const setLastImpulse = useSetLastImpulse();
  const navigate = useNavigate();
  const { saleId } = useSaleExpiration();
  const subscriptionUrl = useMatch(SUBSCRIPTION.url());
  const isMainScreen = useMatchMain();

  const saleCondition = React.useMemo(() => {
    return (
      saleId &&
      lastSeen !== saleId &&
      forSales.includes(saleId) &&
      !subscriptionFlags.hasActiveYearlySub &&
      !subscriptionFlags.hasActiveFamilySub &&
      hintWasSeen(TUTORIALDONE) &&
      viewer &&
      viewer.createdBeforeDays >= 1
    );
  }, [saleId, lastSeen, subscriptionFlags, hintWasSeen, viewer]);

  React.useEffect(() => {
    if (saleCondition && saleId !== SALE.blackFriday) {
      setOpen(true);
    }
  }, [saleCondition, saleId]);

  React.useEffect(() => {
    if (open) setLastImpulse("sale");
  }, [open, setLastImpulse]);

  const handleClose = React.useCallback(() => {
    setOpen(false);
    setLastSeen(saleId);
  }, [saleId, setLastSeen]);

  const handleSubscribeClick = React.useCallback(() => {
    setOpen(false);
    setLastSeen(saleId);
    navigate(SUBSCRIPTION.url());
  }, [navigate, saleId, setLastSeen]);

  React.useEffect(() => {
    if (subscriptionUrl) handleClose(); // if user comes from notification directly on subscription page, close the dialog
  }, [subscriptionUrl, handleClose]);

  if (isMainScreen && saleCondition && saleId === SALE.blackFriday) {
    return <SaleMainBlackFriday />;
  }

  return (
    <SDialogWindow open={open} onClose={handleClose} $saleId={saleId}>
      <Hero>
        {saleId === SALE.christmasFamily && <SaleMainChristmas />}
        {saleId === SALE.motherLangDay && <SaleMainMotherLanguage />}
        {saleId === SALE.summer && <SaleMainSummer />}
      </Hero>

      <SButton variant="contained" color="primary" size="large" onClick={handleSubscribeClick}>
        <Trans>See options</Trans>
      </SButton>
    </SDialogWindow>
  );
};

export default SaleMainDialog;
